import React, { useEffect, useState } from 'react';
import { apiFetch } from "../../../../apiFetch";
import './PlayerReporting.css';

const normalizeLink = (link) => {
    if (!link) return "";
    if (/^https?:\/\//i.test(link)) return link;
    return `${window.location.origin}${link.startsWith("/") ? "" : "/"}${link}`;
};

const PlayerReporting = ({ userId: propUserId }) => {
    const userId = propUserId || localStorage.getItem('playerId');
    const [activeTab, setActiveTab] = useState('reporting');

    const [form, setForm] = useState({
        typeOfUpdate: '',
        description: '',
        eventDate: '',
        contactName: '',
        profileLink: '',
        organizationName: '',
        location: '',
        contactInfo: '',
        files: null
    });

    const [submittedReports, setSubmittedReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [errorReports, setErrorReports] = useState(null);

    useEffect(() => {
        if (userId) {
            console.log('✅ PlayerReporting loaded for user ID:', userId);
        } else {
            console.warn('⚠️ No user ID available in props or localStorage');
        }
    }, [userId]);

    useEffect(() => {
        if (activeTab === 'send' && userId) {
            fetchSubmittedReports();
        }
    }, [activeTab, userId]);

    const fetchSubmittedReports = async () => {
        setLoadingReports(true);
        setErrorReports(null);

        try {
            const res = await apiFetch(`/api/report/${userId}`);

            if (!res.ok) {
                const text = await res.text();
                throw new Error(`Fetch reports failed: ${res.status} ${text}`);
            }

            let data = [];
            try {
                data = await res.json();
            } catch {
                data = [];
            }
            setSubmittedReports(Array.isArray(data) ? data : []);

        } catch (err) {
            console.error('Error fetching reports:', err);
            setErrorReports('Server error. Please try again later.');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setForm((prev) => ({ ...prev, files: e.target.files }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!userId) {
            alert('User not logged in. Please log in again.');
            return;
        }

        const profileLink = form.profileLink.trim();

        if (
            profileLink &&
            !/^(https?:\/\/[^/]+)?\/(scout-profile|agent-profile|club-profile)\/[a-z0-9-]+\/?$/i.test(profileLink)
        ) {
            alert(
                "Please enter a valid VSN profile link (e.g., /scout-profile/ghaucho or https://www.virtualscoutingnetwork.com/scout-profile/ghaucho)."
            );
            return;
        }

        const formData = new FormData();
        formData.append('userId', userId);

        Object.keys(form).forEach((key) => {
            if (key === 'files' && form.files) {
                for (let i = 0; i < form.files.length; i++) {
                    formData.append('files', form.files[i]);
                }
            } else if (key === 'profileLink') {
                formData.append('profileLink', profileLink);
            } else {
                formData.append(key, form[key]);
            }
        });

        try {
            const res = await apiFetch("/api/report", {
                method: "POST",
                body: formData, // ✅ do NOT set Content-Type for FormData
            });

            // ✅ Safe JSON parsing (prevents crash if response isn't JSON)
            let data = {};
            try {
                data = await res.json();
            } catch {
                data = {};
            }

            if (res.ok) {
                alert('Report submitted successfully!');

                setForm({
                    typeOfUpdate: '',
                    description: '',
                    eventDate: '',
                    contactName: '',
                    profileLink: '',
                    organizationName: '',
                    location: '',
                    contactInfo: '',
                    files: null
                });

                if (activeTab === 'send') {
                    fetchSubmittedReports();
                }
            } else {
                alert(`Error: ${data.message || 'Something went wrong'}`);
            }
        } catch (err) {
            console.error('Error submitting report:', err);
            alert('Server error. Please try again later.');
        }
    };

    const updateOptions = [
        'Signed with Agent on VSN',
        'Signed with Scout on VSN',
        'Signed with Club on VSN',
        'Officially Joined a Club',
        'Officially Signed a Contract with Agent',
        'Officially Signed a Contract with Scout',
        'Trial Invitation Received',
        'Invited for Training Camp',
        'Scholarship Awarded',
        'Signed Sponsorship Deal',
        'Received Offer Letter',
        'Transfer to New Club',
        'Selected for National Team',
        'Report Irregularities by Agent, Scout, or Club',
        'Other (Please Specify)',
    ];

    return (
        <div>
            {/* Title & Toggle */}
            <h2 className="main-title">Player Reporting</h2>
            <div className="toggle-switch">
                <button
                    className={activeTab === 'reporting' ? 'active' : ''}
                    onClick={() => setActiveTab('reporting')}
                >
                    Player Reporting
                </button>
                <button
                    className={activeTab === 'send' ? 'active' : ''}
                    onClick={() => setActiveTab('send')}
                >
                    Submitted Reports
                </button>
            </div>

            {/* Main Card */}
            <div className="player-reporting-container">
                {activeTab === 'reporting' && (
                    <>
                        <h3 className="form-title">Player Reporting</h3>
                        <p className="form-description">
                            Please report major career updates, especially those made through VSN. This helps us support your journey and follow up when needed.
                        </p>

                        <form onSubmit={handleSubmit} className="report-form">
                            <label>Type of Update:</label>
                            <select name="typeOfUpdate" value={form.typeOfUpdate} onChange={handleChange} required>
                                <option value="">-- Select an update --</option>
                                {updateOptions.map((opt, index) => (
                                    <option key={index} value={opt}>{opt}</option>
                                ))}
                            </select>

                            <label>Brief Description:</label>
                            <textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Explain what happened, who was involved, and how it happened..."
                                rows={4}
                                required
                            />

                            <label>Date of Event:</label>
                            <input
                                type="date"
                                name="eventDate"
                                value={form.eventDate}
                                onChange={handleChange}
                                required
                            />

                            <label>Who did you sign with / work with?</label>
                            <input
                                type="text"
                                name="contactName"
                                placeholder="Name of person or contact"
                                value={form.contactName}
                                onChange={handleChange}
                            />

                            <label>Enter VSN Profile Link of the Agent, Scout, or Club you worked with:</label>
                            <input
                                type="text"
                                name="profileLink"
                                placeholder="e.g. /scout-profile/ghaucho or https://www.virtualscoutingnetwork.com/scout-profile/ghaucho"
                                value={form.profileLink}
                                onChange={handleChange}
                            />

                            <label>Agency/ Organization / Club Name:</label>
                            <input
                                type="text"
                                name="organizationName"
                                value={form.organizationName}
                                onChange={handleChange}
                            />

                            <label>Location (City, Country):</label>
                            <input
                                type="text"
                                name="location"
                                value={form.location}
                                onChange={handleChange}
                            />

                            <label>Contact Info (email, phone):</label>
                            <input
                                type="text"
                                name="contactInfo"
                                value={form.contactInfo}
                                onChange={handleChange}
                            />

                            <label>Upload Proof (optional):</label>
                            <input
                                type="file"
                                name="files"
                                onChange={handleFileChange}
                                multiple
                            />

                            <p className="privacy-note">
                                Your report is private and reviewed only by the VSN team to verify and support your career.
                            </p>

                            <button type="submit" className="submit-button">Submit Report</button>
                        </form>
                    </>
                )}

                {activeTab === 'send' && (
                    <div className="submitted-reports">
                        {loadingReports && <p>Loading submitted reports...</p>}
                        {errorReports && <p style={{ color: 'red' }}>{errorReports}</p>}
                        {!loadingReports && submittedReports.length === 0 && !errorReports && (
                            <p>No submitted reports found.</p>
                        )}
                        {submittedReports.map((report, idx) => (
                            <div key={idx} className="submitted-report-card">
                                <h3 className="submitted-title">Submitted Report</h3>
                                <h4><strong>Title:</strong> {report.typeOfUpdate}</h4>
                                <p><strong>Description:</strong> {report.description}</p>
                                <p><strong>Date:</strong> {new Date(report.eventDate).toLocaleDateString()}</p>
                                <p><strong>Who did you sign/work with:</strong> {report.contactName}</p>
                                <p><strong>VSN Profile Link:</strong> <a href={normalizeLink(report.profileLink)} target="_blank" rel="noreferrer">{report.profileLink}</a></p>
                                <p><strong>Agency / Organization / Club Name:</strong> {report.organizationName}</p>
                                <p><strong>Location (City, Country):</strong> {report.location}</p>
                                <p><strong>Contact Info (email, phone):</strong> {report.contactInfo}</p>
                                {report.fileUrls?.length > 0 && (
                                    <div>
                                        <strong>Upload Proof (optional):</strong>
                                        <ul>
                                            {report.fileUrls.map((url, i) => (
                                                <li key={i}>
                                                    <a href={normalizeLink(url)} target="_blank" rel="noreferrer">
                                                        View File {i + 1}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlayerReporting;














